/**
 * adminApiCache — short-lived in-memory cache for API GET requests, used by the
 * whole admin panel (and, since the module loads with the app, any other page).
 *
 * Every store slice talks to the API with `fetch(...)`. Instead of adding a cache
 * to each of the ~85 call sites, this wraps `window.fetch` once:
 *
 *   • GET requests to the API are served from an in-memory cache while they are
 *     still fresh (default 60s) — revisiting a page does not refetch.
 *   • ANY write (POST/PATCH/PUT/DELETE) that succeeds clears the cache, so every
 *     page revalidates after a change instead of showing stale data.
 *   • `invalidateAdminCache()` clears it explicitly (also called on admin
 *     login/logout and after a successful admin mutation).
 *   • The cache key includes the bearer token, so one user never sees another
 *     user's cached response.
 */
import { API_URL } from "../constants";

/** How long a GET response is served from cache. */
export const ADMIN_CACHE_TTL_MS = 60 * 1000;

/** `${token}::${url}` → { at, status, statusText, headers, body } */
const cache = new Map();

/** `${token}::${url}` → Promise<Response> for a request already on the wire. */
const inFlight = new Map();

/**
 * When the API answers 429, stop sending more GETs for a short window. Without
 * this, navigating between admin pages keeps firing every page's initial
 * fetches and deepens the rate-limit ban.
 */
const RATE_LIMIT_COOLDOWN_MS = 15 * 1000;
let rateLimitedUntil = 0;

/** Headers that must not be replayed when rebuilding a cached Response. */
const DROP_HEADERS = new Set([
  "content-encoding",
  "content-length",
  "transfer-encoding",
  "connection",
]);

/** Statuses that may not carry a body in a `new Response(...)`. */
const NULL_BODY_STATUSES = new Set([204, 205, 304]);

/** Endpoints that must always hit the network (payment verify, webhooks). */
const NEVER_CACHE = [/\/verify\b/i, /\/webhook\b/i];

function readHeader(headers, name) {
  if (!headers) return "";
  if (typeof headers.get === "function") return headers.get(name) || "";
  const key = Object.keys(headers).find((k) => k.toLowerCase() === name.toLowerCase());
  return key ? String(headers[key]) : "";
}

/** Forget every cached admin GET response. */
export function invalidateAdminCache() {
  cache.clear();
}

/**
 * Install the cache by wrapping `window.fetch` (idempotent). Called from
 * `store/admin/adminUtils.js`, which every admin slice imports.
 */
export function installAdminApiCache({ ttlMs = ADMIN_CACHE_TTL_MS, prefix = "/api/" } = {}) {
  if (typeof window === "undefined" || window.__fillopAdminCache) return;

  const nativeFetch = window.fetch.bind(window);
  const base = String(API_URL || "").replace(/\/$/, "");

  window.fetch = async (input, init = {}) => {
    const url = typeof input === "string" ? input : input?.url || "";
    const method = String(
      init.method || (typeof input === "string" ? "GET" : input?.method) || "GET"
    ).toUpperCase();

    const isApiCall =
      Boolean(base) &&
      url.startsWith(base) &&
      url.includes(prefix) &&
      !NEVER_CACHE.some((pattern) => pattern.test(url));

    // Any successful write invalidates every cached read, so no page shows
    // stale data after a change (approve, delete, update, purchase, …).
    if (isApiCall && method !== "GET" && method !== "HEAD") {
      const response = await nativeFetch(input, init);
      if (response.ok) cache.clear();
      return response;
    }

    if (!isApiCall || method !== "GET") return nativeFetch(input, init);

    const headers = init.headers || (typeof input === "string" ? null : input?.headers);
    const key = `${readHeader(headers, "authorization")}::${url}`;

    // While rate-limited, serve the last successful response even if stale;
    // otherwise fail fast without another network request.
    if (rateLimitedUntil > Date.now()) {
      const stale = cache.get(key);
      if (stale) {
        return new Response(NULL_BODY_STATUSES.has(stale.status) ? null : stale.body, {
          status: stale.status,
          statusText: stale.statusText,
          headers: stale.headers,
        });
      }
      return new Response(
        JSON.stringify({
          error: { message: "Too many requests. Please wait a moment and try again." },
        }),
        {
          status: 429,
          statusText: "Too Many Requests",
          headers: { "Content-Type": "application/json" },
        }
      );
    }

    const hit = cache.get(key);
    if (hit && Date.now() - hit.at < ttlMs) {
      return new Response(NULL_BODY_STATUSES.has(hit.status) ? null : hit.body, {
        status: hit.status,
        statusText: hit.statusText,
        headers: hit.headers,
      });
    }

    // React StrictMode (and rapid navigation) can start the same GET twice
    // before the first response lands. Share that request instead of doubling
    // the traffic — otherwise the API rate limiter returns 429.
    const pending = inFlight.get(key);
    if (pending) {
      return (await pending).clone();
    }

    const request = nativeFetch(input, init).then(async (response) => {
      if (response.status === 429) {
        rateLimitedUntil = Date.now() + RATE_LIMIT_COOLDOWN_MS;
      }
      if (response.ok) {
        try {
          const body = await response.clone().text();
          const cachedHeaders = [...response.headers.entries()].filter(
            ([name]) => !DROP_HEADERS.has(name.toLowerCase())
          );
          cache.set(key, {
            at: Date.now(),
            status: response.status,
            statusText: response.statusText,
            headers: cachedHeaders,
            body,
          });
        } catch {
          /* Body not cacheable — just skip caching this response. */
        }
      }
      return response;
    });

    inFlight.set(key, request);
    try {
      return (await request).clone();
    } finally {
      inFlight.delete(key);
    }
  };

  window.__fillopAdminCache = true;
}

/** Exposed for debugging/tests. */
export const adminApiCacheStore = cache;
